import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import SkeletonGroup from '@/components/motion/SkeletonGroup'
import EmptyState from '@/components/ui/EmptyState'
import RefreshRegion from '@/components/ui/RefreshRegion'
import TitleRow from '@/components/ui/TitleRow'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useDailySignals } from '@/features/signals/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import {
  dailyForecastSearchSchema,
  loadDailyForecast,
} from '@/screens/signals/DailyForecastScreen.loader'
import SessionDatePicker from '@/screens/signals/components/SessionDatePicker'
import SignalTileBoard from '@/screens/signals/components/SignalTileBoard'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import SignalsToolbar from '@/screens/signals/components/SignalsToolbar'
import { TileBoardSkeleton } from '@/screens/signals/components/SignalsTableSkeletons'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { onSession, useSessionStatus } from '@/screens/signals/lib/session-status'
import { useShownValue } from '@/screens/signals/lib/shown-value'
import { isSignalsLocked } from '@/screens/signals/lib/signals-ui'

const TITLE = 'Daily Forecast'

/** The strip names the page, so the header is the bare title (`sr-only` from `lg`). */
const HEADER = <TitleRow title={TITLE} />

const EMPTY_NOTE = {
  green: 'No green signals on this session.',
  red: 'No red signals on this session.',
} as const

/*
 * The loaded page, block for block: the tabs with the date at their right and
 * a board of tiles. Board sizes are data, so twelve stand in.
 */
function DailyForecastPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the daily forecast…"
      header={HEADER}
      {...signalsRailProps(TITLE)}
    >
      <TileBoardSkeleton />
    </ScreenLayout>
  )
}

function DailyForecastError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={HEADER}
      pageTitle={TITLE}
      title="The daily forecast did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/daily')({
  validateSearch: dailyForecastSearchSchema,
  loader: loadDailyForecast,
  pendingComponent: DailyForecastPending,
  errorComponent: DailyForecastError,
  component: DailyForecastScreen,
})

/**
 * Signal Zone · Daily forecast — one session's after-close green and red
 * boards, strongest first, one side at a time as a grid of symbol tiles (Live's board).
 *
 * MAIN read keyed on `?date=`, so `useQuery` + `keepPreviousData`, not
 * suspense (docs/route-loading.md, "Search params"): picking a session keeps
 * the old board on screen, under RefreshRegion's refresh line, until the new
 * one lands — no skeleton, and no entrance replayed. A session that fails to
 * load is region-local; a 403 is the locked state (D4).
 *
 * `data: null` is mm-be's answer both for a session with no signal on either
 * board and for one nothing was computed for, so only a day `/sessions/`
 * skips gets the session's own empty state.
 *
 * The tabs say "Green signals", not the frame's "…for today": a past
 * session, or the default one before the 16:45 run, is not today (audit M2).
 * The session is the date at the tabs' right, in BS (D5; owner 2026-10-12).
 */
function DailyForecastScreen() {
  const search = Route.useSearch()
  const { date } = search
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useDailySignals(date ?? null)
  const view = resolveViewState(query, { online })
  const board = query.data
  const isUpdating = query.isPlaceholderData
  // The session the answer on screen is for, while a pick loads.
  const session = useSessionStatus(useShownValue(date ?? null, isUpdating))

  // The session picker sits at the tabs' right (the strip names the page, so the title row is
  // bare).
  const picker = (
    <>
      <UpdatingNote when={isUpdating} />
      <SessionDatePicker
        value={date ?? board?.date ?? null}
        onChange={(next) => {
          void navigate({ search: (prev) => ({ ...prev, date: next }) })
        }}
      />
    </>
  )

  return (
    <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
      {view === 'failed' || view === 'offline' ? (
        isSignalsLocked(query.error) ? (
          <SignalsLocked error={query.error} />
        ) : (
          <div>
            <SignalsToolbar end={picker} />
            <EmptyState
              kind={view}
              size="page"
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
              meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
              {...(view === 'failed' ? { title: 'Couldn’t load that session' } : {})}
            />
          </div>
        )
      ) : board === undefined ? (
        // Only before any answer at all — the loader has one for the entry
        // URL, and a session change keeps the previous board meanwhile.
        <SkeletonGroup className="flex flex-col gap-6">
          <TileBoardSkeleton />
        </SkeletonGroup>
      ) : board === null ? (
        <div>
          <SignalsToolbar end={picker} />
          <RefreshRegion updating={isUpdating}>
            {session.computed === false ? (
              <SignalsEmpty reason="session" date={session.date} />
            ) : (
              <SignalsEmpty
                reason="list"
                title={`No signals ${onSession(session.date)}`}
                description="No stock got a green or a red signal on that session. Pick another session."
              />
            )}
          </RefreshRegion>
        </div>
      ) : (
        <>
          <RefreshRegion updating={isUpdating}>
            <SignalTileBoard
              from="daily"
              rows={board}
              side={search.side}
              onSideChange={(side) => {
                void navigate({ search: (prev) => ({ ...prev, side }) })
              }}
              emptyNote={EMPTY_NOTE}
              toolbarEnd={picker}
            />
          </RefreshRegion>
        </>
      )}
    </ScreenLayout>
  )
}
